import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { GlobalVariableRegistry, DecisionVariablesRegistry, VariableType, VariableAssignment, ToolCall, DecisionCondition } from "../../utils/types";
import {
  updateDecisionNodeEdges
} from "../../utils/utils";
import { operatorNeedsValue } from "../../utils/validation";
import { 
  ModalState
} from "../../hooks/hooks";
import DecisionRuleBuilder from "../DecisionRuleBuilder";
import { NodeContentForm } from "./shared/NodeContentForm";
import ToolCallModal from "./ToolCallModal";

interface NodeModalProps {
  modalState: ModalState;
  isFirstNode: boolean;
  identifiers: string[];
  searchText: string;
  globalVariableRegistry: GlobalVariableRegistry;
  decisionVariablesRegistry: DecisionVariablesRegistry;
  nodes: any[];
  edges: any[];
  setModalState: (state: ModalState | ((prev: ModalState) => ModalState)) => void;
  setEdges: (edges: any[] | ((prev: any[]) => any[])) => void;
  setSearchText: (text: string) => void;
  onOk: () => void;
  onCancel: () => void;
  addIdentifier: (value: string) => void;
  hasChildWithIdentifier: (parentId: string, identifier: string) => boolean;
  getDisabledIdentifiers: (parentId: string) => string[];
  getAvailableIdentifiers: (excludeNodeId?: string) => string[];
  isIdentifierInUse: (identifier: string, excludeNodeId?: string) => boolean;
  onCreateDecisionVariable?: (variableData: { name: string; type: VariableType }) => void;
  onCreateNodeForDecisionPath?: (pathType: "true" | "false", sourceNodeId: string, isTemporary?: boolean) => Promise<string>;
}

const NodeModal: React.FC<NodeModalProps> = ({
  modalState,
  isFirstNode,
  identifiers,
  searchText,
  globalVariableRegistry,
  decisionVariablesRegistry,
  nodes,
  edges,
  setModalState,
  setEdges,
  setSearchText,
  onOk,
  onCancel,
  addIdentifier,
  hasChildWithIdentifier,
  getDisabledIdentifiers,
  getAvailableIdentifiers,
  isIdentifierInUse,
  onCreateDecisionVariable,
  onCreateNodeForDecisionPath: handleCreateNodeForPath,
}) => {
  const [newVariableType, setNewVariableType] = useState<VariableType>("boolean");
  const [hasUserSelectedEntityType, setHasUserSelectedEntityType] = useState(false);
  const [isToolCallModalVisible, setIsToolCallModalVisible] = useState(false);
  const [editingToolCallIndex, setEditingToolCallIndex] = useState<number>(-1);
  const [newToolCallDraft, setNewToolCallDraft] = useState<ToolCall | null>(null);
  const [botContentMode, setBotContentMode] = useState<"content" | "toolcalls">("content");
  const [hasUserEditedIdentifier, setHasUserEditedIdentifier] = useState(false);
  
  // Track original content when modal opens for edit mode to detect content changes
  const [originalContent, setOriginalContent] = useState<string>("");
  // Track original identifier to detect if user has edited it in edit mode
  const [originalIdentifier, setOriginalIdentifier] = useState<string>("");
  
  // Ref to store pending auto-generated identifier
  const pendingAutoIdentifierRef = useRef<string | null>(null);

  // Clear nextNodeId when node becomes a destination node
  useEffect(() => {
    if (modalState.isDestinationNode && modalState.nextNodeId) {
      setModalState((prev: ModalState) => ({ ...prev, nextNodeId: "" }));
    }
  }, [modalState.isDestinationNode, modalState.nextNodeId, setModalState]);

  // Initialize identifier tracking when modal opens (ONLY when modal becomes visible)
  useEffect(() => {
    if (modalState.visible) {
      if (modalState.isEditMode) {
        // EDIT MODE: If there's an existing identifier, it was manually set previously
        setOriginalIdentifier(modalState.newNodeIdentifier);
        const hasExistingIdentifier = modalState.newNodeIdentifier && modalState.newNodeIdentifier.trim() !== "";
        setHasUserEditedIdentifier(hasExistingIdentifier);
      } else {
        // NEW NODE MODE: Start fresh, allow auto-generation
        setOriginalIdentifier("");
        setHasUserEditedIdentifier(false);
      }
    }
  }, [modalState.visible, modalState.isEditMode]); // CRITICAL: Remove newNodeIdentifier from deps to prevent re-triggering

  // Create comprehensive placeholder registry that includes placeholders from all nodes
  const allPlaceholders = useMemo(() => {
    const placeholders: Record<string, any> = { ...globalVariableRegistry };
    
    // Scan all nodes for placeholders that might not be in the global registry
    nodes.forEach((node: any) => {
      const content = node.data?.content || "";
      const regex = /{([^{}]+)}/g;
      let match;
      while ((match = regex.exec(content)) !== null) {
        const placeholderName = match[1];
        if (!placeholders[placeholderName] && !decisionVariablesRegistry[placeholderName]) {
          // Only add if it's not already a decision variable
          placeholders[placeholderName] = {
            name: placeholderName,
            values: [],
            persist: false
          };
        }
      }
      
      // Also check tool call parameters for placeholders
      if (node.data?.toolCalls) {
        node.data.toolCalls.forEach((toolCall: ToolCall) => {
          // Check parameters for placeholders
          toolCall.parameters?.forEach((param: any) => {
            if (param.value) {
              const toolCallRegex = /{([^{}]+)}/g;
              let tcMatch;
              while ((tcMatch = toolCallRegex.exec(param.value)) !== null) {
                const placeholderName = tcMatch[1];
                if (!placeholders[placeholderName] && !decisionVariablesRegistry[placeholderName]) {
                  // Only add if it's not already a decision variable
                  placeholders[placeholderName] = {
                    name: placeholderName,
                    values: [],
                    persist: false
                  };
                }
              }
            }
          });
          
          // Include response variables as placeholders
          toolCall.responseVariables?.forEach((responseVar: any) => {
            if (responseVar.name && !placeholders[responseVar.name] && !decisionVariablesRegistry[responseVar.name]) {
              // Only add if it's not already a decision variable
              placeholders[responseVar.name] = {
                name: responseVar.name,
                values: responseVar.values || [],
                persist: false
              };
            }
          });
        });
      }
    });
    
    return placeholders;
  }, [nodes, globalVariableRegistry, decisionVariablesRegistry]);

  // Auto-set entity type based on parent when adding nodes (but allow user override)
  useEffect(() => {
    if (modalState.visible && modalState.sourceNodeId && !modalState.isEditMode && !hasUserSelectedEntityType) {
      const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
      if (sourceNode) {
        const sourceEntityType = sourceNode.data.entityType;
        
        // Skip auto-setting for DECISION parents since those have restrictions
        if (sourceEntityType !== "DECISION") {
          const oppositeType = sourceEntityType === "BOT" ? "USER" : "BOT";
          setModalState((prev: ModalState) => ({ ...prev, newNodeEntityType: oppositeType }));
        }
      }
    }
  }, [modalState.visible, modalState.sourceNodeId, modalState.isEditMode, hasUserSelectedEntityType, nodes, setModalState]);

  // Track original content when modal opens or content changes
  useEffect(() => {
    if (modalState.visible) {
      if (modalState.isEditMode) {
        // In edit mode, capture the original content (don't reset identifier tracking)
        setOriginalContent(modalState.newNodeContent);
      } else {
        // In add mode, reset original content
        setOriginalContent("");
      }
    }
  }, [modalState.visible, modalState.isEditMode, modalState.newNodeContent]);

  // Set botContentMode based on the node's existing state when modal opens
  useEffect(() => {
    // Only set the initial mode when the modal becomes visible, don't interfere with user selections
    if (modalState.visible) {
      if (modalState.isEditMode) {
        // If editing and the node has tool calls enabled, open in tool calls mode
        if (modalState.hasToolCall && modalState.toolCalls && modalState.toolCalls.length > 0) {
          setBotContentMode("toolcalls");
        } else {
          // Otherwise, open in content mode
          setBotContentMode("content");
        }
      } else {
        // For new nodes, always start with content mode
        setBotContentMode("content");
      }
    }
  }, [modalState.visible, modalState.isEditMode]);

  // Watch for identifier changes to trigger save when auto-generation completes
  useEffect(() => {
    if (pendingAutoIdentifierRef.current && modalState.newNodeIdentifier === pendingAutoIdentifierRef.current) {
      // The auto-generated identifier has been applied to modal state, now save
      pendingAutoIdentifierRef.current = null;
      onOk();
    }
  }, [modalState.newNodeIdentifier, onOk]);

  const handleIdentifierChange = (value: string) => {
    setModalState((prev: ModalState) => ({ ...prev, newNodeIdentifier: value }));
    
    // CRITICAL: Mark that user has manually edited the identifier
    // This prevents auto-generation from overriding manual changes
    // ANY manual change to the identifier field counts as "user edited"
    setHasUserEditedIdentifier(true);
  };

  const handleCreateVariable = () => {
    // Create the variable immediately like StandaloneModal
    const newVariable = { name: searchText, type: newVariableType };
    if (onCreateDecisionVariable) {
      onCreateDecisionVariable(newVariable);
    }
    setSearchText("");
  };

  // Helper function to generate identifier from content
  const generateIdentifierFromContent = useCallback((content: string): string => {
    if (!content.trim()) return "";
    
    // Generate base identifier
    const baseIdentifier = content
      .toUpperCase() // Changed from toLowerCase to toUpperCase
      // Remove placeholders content but keep the variable names
      .replace(/\{([^}]+)\}/g, "$1") // {to_name} becomes TO_NAME
      // Remove special characters and punctuation except spaces and underscores
      .replace(/[^A-Z0-9\s_]/g, "") // Changed regex to match uppercase
      // Replace multiple spaces with single space
      .replace(/\s+/g, " ")
      .trim()
      // Replace spaces with underscores
      .replace(/\s/g, "_")
      // Remove multiple underscores
      .replace(/_+/g, "_")
      // Remove leading/trailing underscores
      .replace(/^_+|_+$/g, "");

    // Check for conflicts and add number suffix if needed
    // Build a Set of existing identifiers for O(1) lookups (excluding current node being edited)
    const existingIdentifiers = new Set(
      nodes
        .filter(node => node.id !== modalState.nodeToEdit)
        .map(node => node.data.identifier)
        .filter(Boolean)
    );
    
    let finalIdentifier = baseIdentifier;
    let counter = 1;
    
    // Keep trying with number suffixes until we find an unused identifier
    while (existingIdentifiers.has(finalIdentifier)) {
      finalIdentifier = `${baseIdentifier}_${counter}`;
      counter++;
    }
    
    return finalIdentifier;
  }, [nodes, modalState.nodeToEdit]);

  // Helper function to check if adding to a decision node
  const isAddingToDecisionNode = () => {
    if (modalState.sourceNodeId) {
      const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
      return sourceNode?.data?.entityType === "DECISION";
    }
    return false;
  };

  const handleDecisionEdgeUpdate = (decisionData: any) => {
    // Only call updateDecisionNodeEdges when in edit mode and editing an existing decision node
    if (modalState.isEditMode && modalState.nodeToEdit) {
      updateDecisionNodeEdges(modalState.nodeToEdit, decisionData, edges, setEdges, nodes);
    }
  };

  const getEntityTypeTooltips = (): Record<"USER" | "BOT" | "DECISION", string> => {
    const tooltips: Record<string, string> = {};
    
    // Add tooltips for disabled options when parent is DECISION
    if (isAddingToDecisionNode()) {
      tooltips.USER = "USER nodes are not allowed after DECISION nodes";
      tooltips.DECISION = "DECISION nodes are not allowed after another DECISION node";
    }
    
    return tooltips;
  };

  const getAllowedEntityTypes = (): ("USER" | "BOT" | "DECISION")[] => {
    if (isAddingToDecisionNode()) {
      return ["BOT"]; // Only BOT is allowed after DECISION
    }
    return ["USER", "BOT", "DECISION"];
  };

  const isOkDisabled = () => {
    // Require identifier for decision node targets or when explicitly required
    if (isAddingToDecisionNode() && (!modalState.newNodeIdentifier || !modalState.newNodeIdentifier.trim())) {
      return true;
    }
    
    // Check identifier uniqueness
    if (modalState.newNodeIdentifier && isIdentifierInUse(modalState.newNodeIdentifier, modalState.nodeToEdit)) {
      return true;
    }

    // For DECISION nodes, require both True Path and False Path to be selected, and at least one valid condition
    if (modalState.newNodeEntityType === "DECISION") {
      // Check if paths are missing
      if (!modalState.decisionData.truePath || !modalState.decisionData.falsePath) {
        return true;
      }
      
      // Check if conditions are missing or invalid
      if (!modalState.decisionData.conditions || modalState.decisionData.conditions.length === 0) {
        return true;
      }
      
      // Check if all conditions have required fields
      const hasInvalidConditions = modalState.decisionData.conditions.some((condition: DecisionCondition) => {
        // Variable is always required
        if (!condition.variable || !condition.variable.trim()) {
          return true;
        }
        
        // Check if the variable is valid (exists in decisionVariablesRegistry or is a persistent placeholder)
        const isValidDecisionVariable = decisionVariablesRegistry[condition.variable] !== undefined;
        const isPersistentPlaceholder = globalVariableRegistry[condition.variable]?.persist === true;
        
        if (!isValidDecisionVariable && !isPersistentPlaceholder) {
          return true; // Invalid variable
        }
        
        // Operator is always required
        if (!condition.operator) {
          return true;
        }
        
        // Value is only required for operators that need one
        if (operatorNeedsValue(condition.operator) && (!condition.value || !condition.value.trim())) {
          return true;
        }
        
        return false;
      });
      
      if (hasInvalidConditions) {
        return true;
      }

      return false;
    }

    // For BOT nodes, check based on the selected content mode
    if (modalState.newNodeEntityType === "BOT") {
      if (botContentMode === "content") {
        // In content mode, require content to be present
        return !modalState.newNodeContent.trim();
      } else if (botContentMode === "toolcalls") {
        // In tool calls mode, require at least one valid tool call
        const hasValidToolCalls = modalState.hasToolCall && 
                                modalState.toolCalls.some((tc: any) => tc.name && tc.name.trim());
        return !hasValidToolCalls;
      }
      return false;
    }
    
    // Check for invalid variable assignments
    if (modalState.variableAssignments && modalState.variableAssignments.length > 0) {
      const decisionVariables = Object.values(decisionVariablesRegistry);
      const hasInvalidAssignments = modalState.variableAssignments.some((assignment: VariableAssignment) => {
        const variable = decisionVariables.find(v => v.name === assignment.variableName);
        if (!variable) return false;
        
        const value = assignment.value;
        
        // Check for invalid values based on type
        if (variable.type === "string" && (value === "" || value === null || value === undefined)) {
          return true;
        }
        if (variable.type === "number" && (isNaN(Number(value)) || value === "" || value === null || value === undefined)) {
          return true;
        }
        // Boolean values are always valid (true/false)
        
        return false;
      });
      
      if (hasInvalidAssignments) {
        return true;
      }
    }

    // For USER nodes, require content
    return !modalState.newNodeContent.trim();
  };

  const handleOk = () => {
    // AUTO-GENERATION LOGIC: Only generate identifier if ALL conditions are met:
    // 1. User has NEVER manually edited the identifier field
    // 2. There is content to generate identifier from
    // 3. The generated identifier would be different from current one
    const shouldAutoGenerate = !hasUserEditedIdentifier && 
                               modalState.newNodeContent && 
                               modalState.newNodeContent.trim();
    
    if (shouldAutoGenerate) {
      const generatedIdentifier = generateIdentifierFromContent(modalState.newNodeContent);
      if (generatedIdentifier && generatedIdentifier !== modalState.newNodeIdentifier) {
        // Store the pending identifier and update state
        pendingAutoIdentifierRef.current = generatedIdentifier;
        setModalState((prev: ModalState) => ({ ...prev, newNodeIdentifier: generatedIdentifier }));
        // The useEffect will trigger onOk() when the state update completes
        return;
      }
    }
    
    // No auto-generation needed or user has manually set identifier - proceed with save
    onOk();
  };

  // Tool call modal handlers
  const handleEditToolCall = (index: number) => {
    setEditingToolCallIndex(index);
    if (index < 0) {
      // Creation mode: seed a stable draft object to avoid re-renders resetting fields
      setNewToolCallDraft({ name: "", description: "", parameters: [], responseVariables: [] });
    } else {
      setNewToolCallDraft(null);
    }
    setIsToolCallModalVisible(true);
  };

  const handleToolCallModalOk = (nodeId: string, toolCall: ToolCall) => {
    if (editingToolCallIndex >= 0) {
      setModalState((prev: ModalState) => {
        const prevToolCalls = prev.toolCalls || [];
        const updatedToolCalls = [...prevToolCalls];
        updatedToolCalls[editingToolCallIndex] = toolCall;
        return { ...prev, toolCalls: updatedToolCalls };
      });
    } else {
      // Creating a new tool call: append and ensure tool calls mode is enabled
      setModalState((prev: ModalState) => {
        const prevToolCalls = prev.toolCalls || [];
        const updatedToolCalls = [...prevToolCalls, toolCall];
        return {
          ...prev,
          toolCalls: updatedToolCalls,
          hasToolCall: true,
          newNodeContent: prev.newNodeContent && prev.newNodeContent.trim() ? "" : prev.newNodeContent,
        };
      });
      // Ensure UI reflects tool calls mode
      setBotContentMode("toolcalls");
    }
    setIsToolCallModalVisible(false);
    setEditingToolCallIndex(-1);
    setNewToolCallDraft(null);
  };

  const handleToolCallModalCancel = () => {
    setIsToolCallModalVisible(false);
    setEditingToolCallIndex(-1);
    setNewToolCallDraft(null);
  };

  // Create the DecisionRuleBuilder as custom content
  const renderDecisionContent = () => {
    if (modalState.newNodeEntityType !== "DECISION") return null;
              
              return (
          <div className="mb-4">
            <DecisionRuleBuilder
              decisionData={modalState.decisionData}
              onChange={(decisionData) => setModalState((prev: ModalState) => ({ ...prev, decisionData }))}
              onUpdateEdges={handleDecisionEdgeUpdate}
              availableNodes={nodes
                .filter(node => !node.data.isPlaceholder && node.id !== modalState.nodeToEdit)
                .map(node => ({ 
                  id: node.id, 
                  label: node.data.label || node.data.entityType,
                  entityType: node.data.entityType,
                  content: node.data.content,
                  identifier: node.data.identifier
                }))
              }
              decisionVariablesRegistry={decisionVariablesRegistry}
              globalVariableRegistry={allPlaceholders}
          onCreateNodeForPath={handleCreateNodeForPath ? (pathType: "true" | "false") => {
            // Set flag when creating new nodes
            setModalState((prev: ModalState) => ({ ...prev, hasCreatedDecisionChildren: true }));
            
            if (modalState.isEditMode && modalState.nodeToEdit) {
              // Editing existing decision node - use the decision node as source
              return handleCreateNodeForPath(pathType, modalState.nodeToEdit);
            } else {
              // Creating new decision node - use parent as temporary source, will reposition later
              const tempSourceNodeId = modalState.sourceNodeId || "";
              return handleCreateNodeForPath(pathType, tempSourceNodeId, true); // true = isTemporary
            }
          } : undefined}
                              />
                            </div>
    );
  };
                              
                              return (
    <>
      <NodeContentForm
        isVisible={modalState.visible}
        modalTitle={modalState.modalTitle}
        onCancel={onCancel}
        onOk={handleOk}
        isOkDisabled={isOkDisabled()}
        isLoading={modalState.isReorganizing || false}
        okButtonText={modalState.isReorganizing ? "Saving..." : undefined}
        cancelButtonText={modalState.isReorganizing ? "Saving..." : undefined}
        
        entityType={modalState.newNodeEntityType}
        onEntityTypeChange={(value) => {
          setModalState((prev: ModalState) => ({ ...prev, newNodeEntityType: value }));
          setHasUserSelectedEntityType(true);
          // Reset bot content mode when changing entity type
          if (value === "BOT") {
            setBotContentMode("content");
          }
        }}
        allowedEntityTypes={getAllowedEntityTypes()}
        entityTypeTooltips={getEntityTypeTooltips()}
        
        showPotentialStart={modalState.newNodeEntityType === "BOT"}
        potentialStart={modalState.potentialStart}
        onPotentialStartChange={(value) => setModalState((prev: ModalState) => ({ ...prev, potentialStart: value }))}
        
        showDestinationNode={true}
        isDestination={modalState.isDestinationNode}
        onDestinationChange={(value) => setModalState((prev: ModalState) => ({ ...prev, isDestinationNode: value }))}
        
        showIdentifier={true}
        identifier={modalState.newNodeIdentifier}
        onIdentifierChange={handleIdentifierChange}
        identifierRequired={isAddingToDecisionNode()}
        identifierError={
          modalState.newNodeIdentifier && 
          isIdentifierInUse(modalState.newNodeIdentifier, modalState.nodeToEdit) &&
          !modalState.isReorganizing // Don't show error during delayed close
            ? `Identifier "${modalState.newNodeIdentifier}" is already in use. Please choose a different identifier.`
            : undefined
        }

        identifierPlaceholder={isAddingToDecisionNode() ? "Required: Type identifier name" : "Type identifier name"}
        
        customContent={renderDecisionContent()}
        
        showNextNode={modalState.newNodeEntityType !== "DECISION"}
        nextNodeId={modalState.nextNodeId}
        nextNodeIds={modalState.nextNodeIds}
        onNextNodeChange={(nodeId) => setModalState((prev: ModalState) => ({ ...prev, nextNodeId: nodeId, nextNodeIds: nodeId ? [nodeId] : [] }))}
        onNextNodesChange={(nodeIds) => setModalState((prev: ModalState) => ({ ...prev, nextNodeIds: nodeIds, nextNodeId: nodeIds && nodeIds.length === 1 ? nodeIds[0] : "" }))}
        availableNodes={nodes
          .filter(node => !node.data.isPlaceholder && node.id !== modalState.nodeToEdit && node.id !== modalState.sourceNodeId)
          .map(node => ({ 
            id: node.id, 
            label: node.data.label || node.data.entityType,
            entityType: node.data.entityType,
            identifier: node.data.identifier || node.data.label || node.data.entityType
          }))
        }
        
        showContent={modalState.newNodeEntityType !== "DECISION"}
        content={modalState.newNodeContent}
        onContentChange={(value) => setModalState((prev: ModalState) => ({ ...prev, newNodeContent: value }))}
        contentPlaceholder={isFirstNode ? "Enter what this entity says in your first node. Use {variable_name} for placeholders." : "Enter what this entity says. Use {variable_name} for placeholders."}
        contentDisabled={modalState.newNodeEntityType === "BOT" && botContentMode === "toolcalls"}
        contentDisabledMessage="Content is disabled when Tool Calls mode is selected. Switch to Content mode to edit text content."
        
        showToolCalls={modalState.newNodeEntityType === "BOT"}
        hasToolCall={modalState.hasToolCall}
        onHasToolCallChange={(value) => setModalState((prev: ModalState) => ({ ...prev, hasToolCall: value }))}
        toolCalls={modalState.toolCalls}
        onToolCallsChange={(toolCalls) => setModalState((prev: ModalState) => ({ ...prev, toolCalls }))}
        onEditToolCall={handleEditToolCall}
        
        botContentMode={botContentMode}
        onBotContentModeChange={(mode) => {
          setBotContentMode(mode);
          // Handle mutual exclusivity - when switching modes, clear/reset appropriate fields
          if (mode === "toolcalls") {
            // When switching to tool calls, clear content and enable tool calls
            setModalState((prev: ModalState) => ({ 
              ...prev, 
              newNodeContent: "", 
              hasToolCall: true 
            }));
          } else {
            // When switching to content, disable tool calls
            if (modalState.hasToolCall) {
              setModalState((prev: ModalState) => ({ ...prev, hasToolCall: false, toolCalls: [] }));
            }
          }
        }}
        
        showVariableAssignment={true}
        existingVariables={Object.values(decisionVariablesRegistry).filter(variable => {
          // Filter out placeholder variables from variable assignment dropdown
          const globalVar = allPlaceholders[variable.name];
          return !globalVar || !globalVar.hasOwnProperty("persist");
        })}
        variableAssignments={modalState.variableAssignments}
        onVariableAssignmentsChange={(assignments) => setModalState((prev: ModalState) => ({ ...prev, variableAssignments: assignments }))}
        onCreateVariable={handleCreateVariable}
        
        searchText={searchText}
        setSearchText={setSearchText}
        newVariableType={newVariableType}
        setNewVariableType={setNewVariableType}
        pendingVariableCreation={null}
        onPendingVariableCreationChange={() => {}}
      />
      
      {/* Tool Call Modal */}
      <ToolCallModal
        visible={isToolCallModalVisible}
        toolCall={editingToolCallIndex >= 0 ? modalState.toolCalls[editingToolCallIndex] : (newToolCallDraft || { name: "", description: "", parameters: [], responseVariables: [] })}
        nodeId={modalState.nodeToEdit || ""}
        globalVariableRegistry={allPlaceholders}
        decisionVariablesRegistry={decisionVariablesRegistry}
        onOk={handleToolCallModalOk}
        onCancel={handleToolCallModalCancel}
      />
    </>
  );
};

export default NodeModal; 